import React, { useState, useEffect } from "react";
import { loadStripe } from '@stripe/stripe-js';
import {
    EmbeddedCheckoutProvider,
    EmbeddedCheckout
} from '@stripe/react-stripe-js';

const CheckoutForm = ({ stripeAccountId, stripeProductId, tipAmount, currency, divisor, setShowCheckoutForm, sourceUrl, sourceType, userId, name, message, visitId, method }) => {
    const visuallyHiddenClass = {
        position: 'absolute',
        width: '1px',
        height: '1px',
        margin: '-1px',
        padding: '0',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        border: '0',
    };
    const [clientSecret, setClientSecret] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY, { stripeAccount: stripeAccountId });

    useEffect(() => {
        let mounted = true;

        const fetchClientSecret = async () => {
            try {
                setIsLoading(true);
                setErrorMessage(null);
                const response = await fetch(`${apiBaseUrl}/create-checkout-session`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        stripeAccountId,
                        stripeProductId,
                        tipAmount: tipAmount * divisor,
                        currency,
                        sourceUrl,
                        sourceType,
                        userId,
                        name,
                        message,
                        visitId,
                        method
                    }),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                if (!data.clientSecret) {
                    throw new Error('No client secret received from the server');
                }

                if (mounted) {
                    setClientSecret(data.clientSecret);
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Error fetching client secret:', error);
                if (mounted) {
                    setErrorMessage('We couldn’t start checkout right now. Please try again later.');
                    setIsLoading(false);
                }
            }
        };

        fetchClientSecret();

        return () => {
            mounted = false;
            setClientSecret(null);
        };
    }, [stripeAccountId, stripeProductId, tipAmount, currency, divisor, sourceUrl, sourceType, userId, name, message, visitId, method]);

    if (isLoading) {
        return (
            <div className="text-white text-center p-8 rounded-lg" role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                <p>Loading...</p>
            </div>
        );
    }

    if (errorMessage) {
        return (
            <div className="w-full">
                <div className="bg-white text-black text-center p-6 rounded-lg max-w-80 mx-auto" role="alert" aria-live="assertive">
                    <p className="mb-4">{errorMessage}</p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={() => setShowCheckoutForm(false)}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700"
                            aria-label="Go back to tip amount selection"
                        >
                            Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!clientSecret) {
        return null;
    }

    return (
        <section id="checkout" aria-labelledby="checkout-heading" className="w-full grid grid-cols-1 md:grid-cols-3 md:gap-4 items-start">
            <h2 id="checkout-heading" style={visuallyHiddenClass}>Payment Details</h2>

            <div className="order-2 md:order-1 flex justify-center md:justify-end pt-4 md:pt-0">
                <button
                    onClick={() => setShowCheckoutForm(false)}
                    className="w-20 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md text-gray-700"
                    aria-label="Go back to tip amount selection"
                >
                    Back
                </button>
            </div>

            <div className="order-1 md:order-2 mx-auto rounded-xl overflow-hidden w-full max-w-96">
                <EmbeddedCheckoutProvider
                    stripe={stripePromise}
                    options={{
                        clientSecret
                    }}
                >
                    <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
            </div>

            <div className="hidden md:block order-3"></div>
        </section>
    );
};

export default CheckoutForm;